// mds-dojo — dojo/html — hoisted from the dijit/layout local port.
//
// Canonical dojo/html.
// Faithful port of references/dojo/html.js — _setNodeContent, _secureForInnerHtml,
// the dojo.html._ContentSetter declare class, and html.set() — which ContentPane's
// _setContent() drives. Uses the faithful declare runtime and the mds parser
// (parser.parse returns a synchronous thenable, so _parse()'s .then() runs inline).
import kernel from './_base/kernel';
import lang from './_base/lang';
import darray from './_base/array';
import declare from './_base/declare-runtime';
import dom from './dom';
import domConstruct from './dom-construct';
import parser from './parser';

// idCounter is incremented with each instantiation to allow assignment of a unique id
var idCounter = 0;

function isArrayLike(it: any) {
  // lang.isArrayLike isn't in the mds lang surface; inline the check used by
  // _setNodeContent (NodeList / array of nodes, but not string/function).
  return it && typeof it.length === 'number' && typeof it != 'string' && typeof it != 'function';
}

var html: any = {

  _secureForInnerHtml: function (cont: any) {
    // removes !DOCTYPE and title elements from the html string.
    return cont.replace(/(?:\s*<!DOCTYPE\s[^>]+>|<title[^>]*>[\s\S]*?<\/title>)/ig, ''); // String
  },

  // Deprecated, should use dojo/dom-construct.empty() directly, remove in 2.0.
  _emptyNode: domConstruct.empty,

  _setNodeContent: function (node: any, cont: any) {
    // inserts the given content into the given node
    domConstruct.empty(node);

    if (cont) {
      if (typeof cont == 'number') {
        cont = cont.toString();
      }
      if (typeof cont == 'string') {
        cont = domConstruct.toDom(cont, node.ownerDocument);
      }
      if (!cont.nodeType && isArrayLike(cont)) {
        // handle as enumerable, but it may shrink as we enumerate it
        for (var startlen = cont.length, i = 0; i < cont.length; i = startlen == cont.length ? i + 1 : 0) {
          domConstruct.place(cont[i], node, 'last');
        }
      } else {
        // pass nodes, documentFragments and unknowns through to dojo.place
        domConstruct.place(cont, node, 'last');
      }
    }

    return node; // DomNode
  },

  // we wrap up the content-setting operation in a object
  _ContentSetter: declare('dojo.html._ContentSetter', null, {
    // node: DomNode|String — parent element that we set content into
    node: '',

    // content: String|DomNode|DomNode[] — content to be placed in the node
    content: '',

    // id: String? — usually only used internally, auto-generated with each instance
    id: '',

    // cleanContent: Boolean
    cleanContent: false,

    // extractContent: Boolean
    extractContent: false,

    // parseContent: Boolean
    parseContent: false,

    // parserScope: String
    parserScope: kernel._scopeName,

    // startup: Boolean
    startup: true,

    // lifecycle methods
    constructor: function (params: any, node: any) {
      // the original params are mixed directly into the instance "this"
      lang.mixin(this, params || {});

      // give precedence to params.node vs. the node argument
      node = this.node = dom.byId(this.node || node);

      if (!this.id) {
        this.id = [
          'Setter',
          (node) ? node.id || node.tagName : '',
          idCounter++
        ].join('_');
      }
    },

    set: function (cont: any, params: any) {
      // front-end to the set-content sequence
      if (undefined !== cont) {
        this.content = cont;
      }
      if (typeof cont == 'number') {
        cont = cont.toString();
      }
      // in the re-use scenario, set needs to be able to mixin new configuration
      if (params) {
        this._mixin(params);
      }

      this.onBegin();
      this.setContent();

      var ret = this.onEnd();

      if (ret && ret.then) {
        // dojox variant returns a Promise that resolves when load+parse complete.
        return ret;
      } else {
        // Vanilla dojo/html._ContentSetter.set() returns a DOMNode for back compat.
        return this.node;
      }
    },

    setContent: function () {
      // sets the content on the node
      var node = this.node;
      if (!node) {
        throw new Error(this.declaredClass + ': setContent given no node');
      }
      try {
        node = html._setNodeContent(node, this.content);
      } catch (e: any) {
        // check if a domfault occurs when appending errorMessage
        var errMess = this.onContentError(e);
        try {
          node.innerHTML = errMess;
        } catch (e2: any) {
          console.error('Fatal ' + this.declaredClass + '.setContent could not change content due to ' + e2.message, e2);
        }
      }
      // always put back the node for the next method
      this.node = node; // DomNode
    },

    empty: function () {
      // cleanly empty out existing content

      // If there is a parse in progress, cancel it. (mds parser.parse returns a
      // synchronous thenable without the isResolved()/cancel() surface, so guard.)
      if (this.parseDeferred) {
        if (this.parseDeferred.isResolved && !this.parseDeferred.isResolved()) {
          this.parseDeferred.cancel();
        }
        delete this.parseDeferred;
      }

      // destroy any widgets from a previous run
      if (this.parseResults && this.parseResults.length) {
        darray.forEach(this.parseResults, function (w: any) {
          if (w.destroy) {
            w.destroy();
          }
        });
        delete this.parseResults;
      }
      domConstruct.empty(this.node);
    },

    onBegin: function () {
      // Called after instantiation, but before set().
      var cont = this.content;

      if (lang.isString(cont)) {
        if (this.cleanContent) {
          cont = html._secureForInnerHtml(cont);
        }

        if (this.extractContent) {
          var match = cont.match(/<body[^>]*>\s*([\s\S]+)\s*<\/body>/im);
          if (match) { cont = match[1]; }
        }
      }

      // clean out the node and any cruft associated with it - like widgets
      this.empty();

      this.content = cont;
      return this.node; // DomNode
    },

    onEnd: function () {
      // Called after set(); optionally run the parser over the new content.
      if (this.parseContent) {
        // populates this.parseResults and this.parseDeferred
        this._parse();
      }
      return this.node; // DomNode
    },

    tearDown: function () {
      // manually reset the Setter instance if it's being re-used
      delete this.parseResults;
      delete this.parseDeferred;
      delete this.node;
      delete this.content;
    },

    onContentError: function (err: any) {
      return 'Error occurred setting content: ' + err;
    },

    onExecError: function (err: any) {
      return 'Error occurred executing scripts: ' + err;
    },

    _mixin: function (params: any) {
      // mix properties/methods into the instance
      var empty: any = {}, key;
      for (key in params) {
        if (key in empty) { continue; }
        this[key] = params[key];
      }
    },

    _parse: function () {
      // runs the dojo parser over the node contents, storing results in
      // this.parseResults and the parse promise in this.parseDeferred
      var rootNode = this.node;
      try {
        var inherited: any = {};
        darray.forEach(['dir', 'lang', 'textDir'], function (this: any, name: string) {
          if (this[name]) {
            inherited[name] = this[name];
          }
        }, this);
        var self = this;
        this.parseDeferred = parser.parse({
          rootNode: rootNode,
          noStart: !this.startup,
          inherited: inherited,
          scope: this.parserScope
        }).then(function (results: any) {
          return self.parseResults = results;
        }, function (e: any) {
          self._onError('Content', e, 'Error parsing in _ContentSetter#' + self.id);
        });
      } catch (e: any) {
        this._onError('Content', e, 'Error parsing in _ContentSetter#' + this.id);
      }
    },

    _onError: function (type: any, err: any, consoleText: any) {
      // shows user the string returned by on[type]Error
      var errText = this['on' + type + 'Error'].call(this, err);
      if (consoleText) {
        console.error(consoleText, err);
      } else if (errText) { // an empty string won't change current content
        html._setNodeContent(this.node, errText, true);
      }
    }
  }), // end declare()

  set: function (node: any, cont: any, params: any) {
    // inserts (replaces) the given content into the given node.
    if (undefined == cont) {
      console.warn('dojo.html.set: no cont argument provided, using empty string');
      cont = '';
    }
    if (typeof cont == 'number') {
      cont = cont.toString();
    }
    if (!params) {
      // simple and fast
      return html._setNodeContent(node, cont, true);
    } else {
      // more options but slower — note the reversed argument order
      var op = new html._ContentSetter(lang.mixin(
        params,
        { content: cont, node: node }
      ));
      return op.set();
    }
  }
};

lang.setObject('dojo.html', html);

export default html;
